import { useEffect, useMemo, useState } from 'react';
import { SidePanelContent } from '~/components/sidepanel-section';
import { P2GBox, P2GButton, P2GText } from '@parcel2go/core-ui';
import { Integration } from '~/lib/api/account-linking/types';
import { useAccountLinkingApi } from '~/lib/api/account-linking/use-account-linking-api';
import IntegrationScreen from './integration-screen';
import { OnboardingTitle } from '../rules/onboarding-title';
import Loading from '~/components/loading/Loading';
import IntegrationSelectionScreen from './integration-selection-screen';
import { OnboardingActionBar } from '../intro/onboarding-action-bar';
import { SettingsCache } from '~/lib/consts/settings-cache';
import JustLinkedScreen from './just-linked';
import { OnboardingScreenContentWrapper } from '../../OnboardingScreenContentWrapper';
import JustFailedScreen from './just-failed';
import { onlyUniqueNonBlank } from '~/lib/utils/array';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

type props = {
  isXs: boolean;
  justLinked: string;
  justFailed: string;
  signupIntegrations?: string[];
  onDone: (outstandingActions: string[], completed: string[]) => void;
  onTodo: (outstandingActions: string[]) => void;
  clearJustFailed: () => void;
  clearJustLinked: () => void;
};

type IntegrationStatus = { id: string; done: boolean; skipped: boolean };

// I've coded it so they can do as many as they want, but UX have asked for a limit of 1.
const numberOfAccountsTheyCanLinkDuringSetup = 1;
const priority = ['ebay', 'amazon', 'etsy', 'shopify'];

const getOutstandingIntegrations = (integrationSelection: IntegrationStatus[]): string[] =>
  integrationSelection.filter((x) => x.skipped || !x.done).map((x) => x.id);

const getDoneIntegrations = (integrationSelection: IntegrationStatus[]): string[] =>
  integrationSelection.filter((x) => x.done).map((x) => x.id);

const AccountsScreen = ({
  signupIntegrations,
  justLinked,
  justFailed,
  clearJustFailed,
  clearJustLinked,
  isXs,
  onDone,
  onTodo
}: props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { getintegrationsummary } = useAccountLinkingApi();
  const [integrations, setAccounts] = useState<Integration[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasShownJustLinked, setHasShownJustLinked] = useState<boolean>(false);
  const [hasShownJustFailed, setHasShownJustFailed] = useState<boolean>(false);

  const [integrationSelection, setIntergrationSelection] = useState<IntegrationStatus[]>(null);

  const activeIntegration = useMemo(() => {
    const firstNotDone = integrationSelection?.find((x) => !x.done && !x.skipped);
    if (!firstNotDone) {
      return null;
    }
    return integrations?.find((x) => x.slug === firstNotDone.id);
  }, [integrations, integrationSelection]);

  const integrationJustLinked = useMemo(
    () => integrations?.find((x) => x.slug === justLinked),
    [integrations, justLinked]
  );

  const integrationJustFailed = useMemo(
    () => integrations?.find((x) => x.slug === justFailed),
    [integrations, justFailed]
  );

  const deferred = useMemo(() => {
    return integrationSelection
      ?.filter((x) => !x.done && !x.skipped && x.id !== activeIntegration?.slug && x.id !== justLinked)
      .map((x) => x.id);
  }, [integrationSelection, activeIntegration, justLinked]);

  const setIntegrationAsFirstInSelection = (id: string) => {
    setIntergrationSelection((current) => {
      // Already first
      if (current.findIndex((x) => x.id === id) === 0) {
        return current;
      }
      return [current.find((x) => x.id === id), ...current.filter((x) => x.id !== id)];
    });
  };

  useEffect(() => {
    const getAccounts = async (): Promise<void> => {
      setLoading(true);
      getintegrationsummary()
        .then((resp) => {
          if (resp.success) {
            const integrationsToAdd = resp.result
              .filter((x) => x.type === 'Marketplace')
              .sort((a, b) => {
                const pA = priority.indexOf(a.slug);
                const pB = priority.indexOf(b.slug);
                return pB - pA;
              });

            // If enabled, add CSV as an option
            if (SettingsCache.settings.Features.includes('CsvUpload')) {
              integrationsToAdd.push({
                slug: 'csv',
                name: 'CSV',
                type: 'Marketplace',
                accounts: [],
                accountActions: []
              });
            }
            setAccounts(integrationsToAdd);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };
    getAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once all done, call onDone with the list of skipped integrations
  useEffect(() => {
    if (integrationSelection !== null) {
      const doneOrSkipped = integrationSelection.filter((x) => x.skipped || x.done).length;
      const allDoneOrSkipped = integrationSelection.every((x) => x.done || x.skipped);
      if (
        doneOrSkipped >= numberOfAccountsTheyCanLinkDuringSetup ||
        allDoneOrSkipped ||
        integrationSelection.length === 0
      ) {
        onDone(getOutstandingIntegrations(integrationSelection), getDoneIntegrations(integrationSelection));
      }
    }
  }, [integrationSelection, onDone]);

  if (loading) {
    return (
      <>
        <OnboardingScreenContentWrapper>
          <P2GBox textAlign="center">
            <SidePanelContent>
              <OnboardingTitle
                text="Loading your sales <span>channels</span>"
                translationKey="SETUP_SALES_CHANNELS_LOADING_TITLE"
              />
              <P2GText variant="subtitle1">
                We’ll import your unfulfilled sales and update them
                <br />
                with tracking details when you ship
              </P2GText>
              <Loading />
            </SidePanelContent>
          </P2GBox>
        </OnboardingScreenContentWrapper>
        <OnboardingActionBar
          skip={
            <P2GButton variant="text" onClick={() => onDone(getOutstandingIntegrations(integrationSelection), [])}>
              {t('SKIP_DO_LATER_BUTTON', { defaultValue: "Skip, I'll do this later" })}
            </P2GButton>
          }
        >
          <P2GText variant="subtitle2" component="div">
            Just loading your sales channels...
          </P2GText>
        </OnboardingActionBar>
      </>
    );
  }

  if (justLinked && !hasShownJustLinked && integrationJustLinked) {
    return (
      <JustLinkedScreen
        isXs={isXs}
        integration={integrationJustLinked}
        onDone={() => {
          clearJustLinked();
          setHasShownJustLinked(true);
        }}
      />
    );
  }

  if (justFailed && !hasShownJustFailed && integrationJustFailed) {
    return (
      <JustFailedScreen
        isXs={isXs}
        integration={integrationJustFailed}
        onDone={() => {
          clearJustFailed();
          setHasShownJustFailed(true);
        }}
      />
    );
  }

  if (integrationSelection == null) {
    return (
      <IntegrationSelectionScreen
        isXs={isXs}
        integrations={integrations}
        justLinked={justLinked}
        signupIntegrations={signupIntegrations}
        onDone={(x) => {
          if (!x || x.length === 0) {
            onDone([], [justLinked].filter(onlyUniqueNonBlank));
          } else {
            onTodo(x);
            setIntergrationSelection(x.map((i) => ({ id: i, done: false, skipped: false })));
          }
        }}
        onSkip={(x) => onDone(x, [])}
        priority={priority}
      />
    );
  }

  if (activeIntegration) {
    return (
      <IntegrationScreen
        integration={activeIntegration}
        deferredIntegrations={deferred}
        doThisIntegration={setIntegrationAsFirstInSelection}
        onComplete={() => {
          // Mark this specific integration as skipped
          const newSelection = [...integrationSelection];
          newSelection.forEach((x) => {
            if (x.id === activeIntegration.slug) {
              x.done = true;
            }
          });
          setIntergrationSelection(newSelection);
        }}
        onSkip={() => {
          // Mark this specific integration as skipped
          const newSelection = [...integrationSelection];
          newSelection.forEach((x) => {
            if (x.id === activeIntegration.slug) {
              x.skipped = true;
            }
          });
          setIntergrationSelection(newSelection);
        }}
        isXs={isXs}
      />
    );
  }

  return null;
};
export default AccountsScreen;
